import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin } from 'lucide-react';
import { getSchoolTimetable } from '@/api/teacher';
import { WEEKDAYS, asList, unwrap } from '@/components/kit/data';
import { useAuth } from '@/stores/auth';
import { useMyClasses } from '../shared';

interface Slot { id: number; class: string; subject: string; teacher: string; weekday: number; day: string; period: number; starts_at: string; ends_at: string; room: string | null; kind: string }
const hm = (s?: string) => (s ?? '').slice(0, 5);
const TONES = ['bg-blue-50 border-blue-200 text-blue-900', 'bg-green-50 border-green-200 text-green-900', 'bg-amber-50 border-amber-200 text-amber-900', 'bg-purple-50 border-purple-200 text-purple-900', 'bg-pink-50 border-pink-200 text-pink-900', 'bg-teal-50 border-teal-200 text-teal-900'];

/** The teaching week as a timetable: today first on phones, a full week grid on bigger screens. Filter to one class. */
export function TimetablePage() {
  const me = useAuth((s) => s.user?.name);
  const classes = useMyClasses();
  const [classId, setClassId] = useState('');
  const q = useQuery({ queryKey: ['teacher-timetable', classId], queryFn: () => getSchoolTimetable(classId ? { class_id: classId } : undefined).then((r) => asList<Slot>(unwrap(r))) });
  const slots = q.data ?? [];
  const todayN = ((new Date().getDay() + 6) % 7) + 1;
  const [day, setDay] = useState(todayN);
  const days = useMemo(() => [1, 2, 3, 4, 5, 6, 7].filter((d) => d <= 5 || slots.some((s) => s.weekday === d)), [slots]);
  const tone = useMemo(() => { const m = new Map<string, string>(); slots.forEach((s) => { if (!m.has(s.subject)) m.set(s.subject, TONES[m.size % TONES.length]); }); return m; }, [slots]);
  const periods = useMemo(() => [...new Set(slots.map((s) => s.period))].sort((a, b) => a - b), [slots]);
  const mine = (s: Slot) => !classId || s.teacher === me;
  const cell = (s: Slot) => (
    <div key={s.id} className={`rounded-xl border px-2.5 py-2 ${s.kind === 'break' ? 'bg-slate-50 border-slate-200 text-slate-500' : tone.get(s.subject)} ${mine(s) ? '' : 'opacity-50'}`}>
      <p className="text-xs font-bold truncate">{s.kind === 'break' ? 'Break' : s.subject}</p>
      <p className="text-[11px] truncate">{s.class}</p>
      <p className="text-[10px] opacity-75">{hm(s.starts_at)}–{hm(s.ends_at)}{s.room ? ` · ${s.room}` : ''}</p>
    </div>
  );
  const ofDay = (d: number) => slots.filter((s) => s.weekday === d).sort((a, b) => a.period - b.period);
  const periodsToday = ofDay(todayN).filter((s) => s.kind !== 'break').length;

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Teaching</p><h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Timetable</h1>
          <p className="text-sm text-slate-500">{classId ? 'The whole class timetable; your periods are highlighted.' : `Your teaching week · ${periodsToday} period${periodsToday === 1 ? '' : 's'} today.`}</p></div>
        <select value={classId} onChange={(e) => setClassId(e.target.value)} className="border border-slate-300 rounded-xl px-3 py-2.5 text-sm bg-white">
          <option value="">My periods</option>{(classes.data ?? []).map((c) => <option key={c.class_id} value={c.class_id}>{c.class} (whole class)</option>)}
        </select>
      </div>

      {q.isLoading ? <p className="text-sm text-slate-500">Loading…</p> : !slots.length ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">No timetable yet. The school office builds it under Timetable.</div>
      ) : (
        <>
          {/* phones: one day at a time */}
          <div className="md:hidden space-y-3">
            <div className="flex gap-1 overflow-x-auto pb-1">{days.map((d) => <button key={d} onClick={() => setDay(d)} className={`px-3.5 py-2 rounded-xl text-sm font-semibold shrink-0 ${day === d ? 'bg-[#0A2540] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>{WEEKDAYS[d]?.slice(0, 3)}{d === todayN ? ' •' : ''}</button>)}</div>
            {!ofDay(day).length ? <p className="text-sm text-slate-500 bg-white rounded-2xl border border-slate-200 p-6 text-center">Nothing on {WEEKDAYS[day]}.</p> : (
              <ul className="space-y-2">{ofDay(day).map((s) => (
                <li key={s.id} className={`rounded-2xl border p-3 flex items-center gap-3 ${s.kind === 'break' ? 'bg-slate-50 border-slate-200' : tone.get(s.subject)} ${mine(s) ? '' : 'opacity-50'}`}>
                  <div className="text-center w-14 shrink-0"><p className="text-sm font-extrabold">{hm(s.starts_at)}</p><p className="text-[10px] opacity-70">{hm(s.ends_at)}</p></div>
                  <div className="flex-1 min-w-0"><p className="font-bold truncate">{s.kind === 'break' ? 'Break' : s.subject}</p><p className="text-xs truncate">{s.class} · Period {s.period}</p></div>
                  {s.room && <span className="text-xs inline-flex items-center gap-1 shrink-0"><MapPin className="w-3 h-3" />{s.room}</span>}
                </li>
              ))}</ul>
            )}
          </div>
          {/* larger screens: the week grid */}
          <div className="hidden md:block bg-white rounded-2xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr><th className="p-2 text-xs text-slate-500 text-left w-20">Period</th>{days.map((d) => <th key={d} className={`p-2 text-xs text-left ${d === todayN ? 'text-[#0A2540]' : 'text-slate-500'}`}>{WEEKDAYS[d]}{d === todayN ? ' (today)' : ''}</th>)}</tr></thead>
              <tbody>
                {periods.map((p) => (
                  <tr key={p} className="border-t border-slate-100 align-top">
                    <td className="p-2 text-xs text-slate-500 font-semibold">P{p}</td>
                    {days.map((d) => <td key={d} className={`p-1.5 ${d === todayN ? 'bg-slate-50/70' : ''}`}><div className="space-y-1">{slots.filter((s) => s.weekday === d && s.period === p).map(cell)}</div></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
